import { ViewConfig } from "@lib/views";
import {
    DATE_COLUMN_SIZE,
    AMOUNT_COLUMN_SIZE,
    CATEGORY_COLUMN_SIZE,
} from "@design-system/ViewTable/DefaultColumnSizes";
import { Category } from "@common/types/category";
import { Entity } from "@common/types/entity";
import { HistoryDetailsBaseRow } from "../../cells/historyDetailsBaseRow";
import { HistoryDetailsDateCell } from "../../cells/HistoryDetailsDateCell";
import { HistoryDetailsEntityCell } from "../../cells/HistoryDetailsEntityCell";
import { HistoryDetailsAmountCell } from "../../cells/HistoryDetailsAmountCell";
import { SplitExpandCell } from "../cells/SplitExpandCell";
import { SplitCategoryCell } from "../cells/SplitCategoryCell";
import { SplitLedgerCell } from "../cells/SplitLedgerCell";
import { SplitDescriptionCell } from "../cells/SplitDescriptionCell";
import { SplitLedgerInfo } from "../schema";

export interface SplitParentTransactionRow extends HistoryDetailsBaseRow {
    id: string;
    sourceId: string;
    bankDescription: string;
    category: Partial<Category>;
    ledger: SplitLedgerInfo;
    amount: number;
    originalAmount: number;
    entity: Partial<Entity>;
    financialAccountId: number | null;
}

export enum SplitParentTransactionColumn {
    DATE = "date",
    DESCRIPTION = "bankDescription",
    CATEGORY = "category",
    LEDGER = "ledger",
    AMOUNT = "amount",
    ENTITY = "entity",
    ACTIONS = "actions",
}

type SplitParentTransactionsViewConfig = ViewConfig<
    SplitParentTransactionRow,
    string,
    never
>;

const CATEGORY_CLASSIFICATION_COLUMN: SplitParentTransactionsViewConfig["columns"] =
    {
        [SplitParentTransactionColumn.CATEGORY]: {
            accessorKey: "category",
            name: "Category",
            header: "Category",
            cell: SplitCategoryCell,
            ...CATEGORY_COLUMN_SIZE,
        },
    };

const LEDGER_CLASSIFICATION_COLUMN: SplitParentTransactionsViewConfig["columns"] =
    {
        [SplitParentTransactionColumn.LEDGER]: {
            accessorKey: "ledger",
            name: "Cash ledger",
            header: "Cash ledger",
            cell: SplitLedgerCell,
            ...CATEGORY_COLUMN_SIZE,
        },
    };

export const createSplitParentTransactionsViewConfig = (
    isGlFirst: boolean,
): SplitParentTransactionsViewConfig => ({
    columns: {
        [SplitParentTransactionColumn.DATE]: {
            accessorKey: "date",
            name: "Date",
            header: "Date",
            cell: HistoryDetailsDateCell,
            immutable: true,
            ...DATE_COLUMN_SIZE,
        },
        [SplitParentTransactionColumn.DESCRIPTION]: {
            accessorKey: "bankDescription",
            name: "Description",
            header: "Description",
            cell: SplitDescriptionCell,
            minSize: 200,
            maxSize: 600,
        },
        ...(isGlFirst
            ? LEDGER_CLASSIFICATION_COLUMN
            : CATEGORY_CLASSIFICATION_COLUMN),
        [SplitParentTransactionColumn.AMOUNT]: {
            accessorKey: "amount",
            name: "Amount",
            header: "Amount",
            cell: HistoryDetailsAmountCell,
            ...AMOUNT_COLUMN_SIZE,
        },
        [SplitParentTransactionColumn.ENTITY]: {
            accessorKey: "entity",
            name: "Entity",
            header: "Entity",
            cell: HistoryDetailsEntityCell,
            minSize: 56,
            maxSize: 150,
        },
        [SplitParentTransactionColumn.ACTIONS]: {
            accessorKey: "id",
            name: "Actions",
            header: "",
            cell: SplitExpandCell,
            minSize: 56,
            maxSize: 56,
            immutable: true,
            hiddenFromSettings: true,
        },
    },
});
